/** Where a token lives and how it is read back (STORY-073 regression).
 *
 * No imports and erasable-only TypeScript syntax on purpose: `scripts/check_token_store.mjs`
 * runs this file straight under Node's type stripping, so the storage rules are tested
 * without a bundler or a test runner.
 *
 * A token is read from whichever storage holds it. Which storage a token was *written* to
 * decides how long it lives (localStorage across tabs and restarts, sessionStorage for one
 * sitting); reading must not depend on that choice, or a persistent token with no refresh
 * token (the desktop host token, a paired phone) becomes invisible.
 */

export function readToken(k: string): string | null {
  try {
    return localStorage.getItem(k) ?? sessionStorage.getItem(k);
  } catch {
    return null; // a locked-down browser is a signed-out browser, never a crash
  }
}

/** Write to the chosen storage; `null` clears the key from both. */
export function writeToken(k: string, v: string | null, persistent: boolean): void {
  try {
    if (v === null) {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    } else (persistent ? localStorage : sessionStorage).setItem(k, v);
  } catch {
    /* ignore */
  }
}

/** Tokens a local host issued (host token, paired phone): LocalGuard in
 * packages/svc-kit/src/svc_kit/guard.py mints them with the `lt.` prefix. They have no
 * refresh token and nothing in the cloud knows them. */
export const isLocalToken = (t: string | null): boolean => t !== null && t.startsWith("lt.");
